import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { exportElementToPDF } from "@/lib/pdfExport";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2, Eye, Search, FileDown, Archive } from "lucide-react";

const API = "/api";

export default function Students() {
  const { getAuthHeader } = useAuth();
  const pageRef = useRef(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    total_purchased_classes: "",
    validity_date: "",
  });

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${API}/students?limit=500`);
      const data = response.data;
      setStudents(Array.isArray(data) ? data : data.items || []);
    } catch (error) {
      console.error("Failed to fetch students:", error);
      toast.error("Failed to load students");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (student = null) => {
    if (student) {
      setSelectedStudent(student);
      setFormData({
        name: student.name,
        email: student.email || "",
        total_purchased_classes: student.total_purchased_classes.toString(),
        validity_date: student.validity_date || "",
      });
    } else {
      setSelectedStudent(null);
      setFormData({ name: "", email: "", total_purchased_classes: "", validity_date: "" });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        email: formData.email ? formData.email.trim().toLowerCase() : null,
        total_purchased_classes: parseInt(formData.total_purchased_classes),
        validity_date: formData.validity_date || null,
      };

      if (selectedStudent) {
        await axios.put(`${API}/students/${selectedStudent.id}`, payload);
        toast.success("Student updated");
      } else {
        await axios.post(`${API}/students`, payload);
        toast.success("Student added");
      }
      setDialogOpen(false);
      fetchStudents();
    } catch (error) {
      console.error("Failed to save student:", error);
      toast.error(error?.response?.data?.detail || "Failed to save student");
    }
  };

  const handleDelete = async () => {
    if (!selectedStudent) return;
    try {
      const response = await axios.delete(`${API}/students/${selectedStudent.id}`, { headers: getAuthHeader() });
      toast.success(response.data?.message || "Student archived");
      setDeleteDialogOpen(false);
      setSelectedStudent(null);
      fetchStudents();
    } catch (error) {
      console.error("Failed to delete student:", error);
      toast.error(error.response?.data?.detail || "Failed to delete student");
    }
  };

  const filteredStudents = students.filter((student) =>
    student.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleExportPDF = async () => {
    if (!pageRef.current) return;
    try {
      toast.info("Generating PDF...");
      await exportElementToPDF(
        pageRef.current,
        `students_report_${new Date().toISOString().split('T')[0]}.pdf`
      );
      toast.success("PDF downloaded successfully!");
    } catch (error) {
      toast.error("Failed to export PDF");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="students-loading">
        <div className="animate-pulse text-slate-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="students-page" ref={pageRef}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Students</h1>
          <p className="text-slate-500 mt-1">Manage student records</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/deleted-students">
            <Button variant="outline" className="text-amber-600 border-amber-200 hover:bg-amber-50" data-testid="deleted-students-link">
              <Archive className="w-4 h-4 mr-2" />
              Deleted
            </Button>
          </Link>
          <Button
            variant="outline"
            onClick={handleExportPDF}
            data-testid="export-pdf-btn"
          >
            <FileDown className="w-4 h-4 mr-2" />
            Export PDF
          </Button>
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-indigo-600 hover:bg-indigo-700 btn-active"
            data-testid="add-student-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Student
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search students..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
          data-testid="search-students-input"
        />
      </div>

      {/* Students Table */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="font-semibold text-slate-600">Student ID</TableHead>
                <TableHead className="font-semibold text-slate-600">Student</TableHead>
                <TableHead className="font-semibold text-slate-600 text-center">Total Classes</TableHead>
                <TableHead className="font-semibold text-slate-600 text-center">Remaining</TableHead>
                <TableHead className="font-semibold text-slate-600">Validity</TableHead>
                <TableHead className="font-semibold text-slate-600 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredStudents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12 text-slate-500">
                    {searchQuery ? "No students found" : "No students yet. Add one to get started."}
                  </TableCell>
                </TableRow>
              ) : (
                filteredStudents.map((student) => (
                  <TableRow
                    key={student.id}
                    className="table-row-hover"
                    data-testid={`student-row-${student.id}`}
                  >
                    <TableCell>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono bg-slate-100 text-slate-600">
                        {student.id.substring(0, 8)}...
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                          <span className="text-indigo-700 font-semibold text-sm">
                            {student.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                          </span>
                        </div>
                        <div className="flex flex-col">
                          <span className="font-medium text-slate-900">{student.name}</span>
                          {student.email ? (
                            <span className="text-xs text-slate-500" data-testid={`student-email-${student.id}`}>
                              ✉️ {student.email}
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleOpenDialog(student)}
                              className="text-xs text-amber-600 hover:text-amber-700 hover:underline text-left"
                              data-testid={`add-email-${student.id}`}
                            >
                              ⚠️ Add email
                            </button>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-center font-medium">
                      {student.total_purchased_classes}
                    </TableCell>
                    <TableCell className="text-center">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${
                          student.remaining_classes <= 3
                            ? "bg-amber-100 text-amber-800"
                            : "bg-emerald-100 text-emerald-800"
                        }`}
                      >
                        {student.remaining_classes}
                      </span>
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {student.validity_date || "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Link to={`/students/${student.id}`}>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-slate-600 hover:text-indigo-600"
                            data-testid={`view-student-${student.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </Link>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDialog(student)}
                          className="text-slate-600 hover:text-indigo-600"
                          data-testid={`edit-student-${student.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedStudent(student);
                            setDeleteDialogOpen(true);
                          }}
                          className="text-slate-600 hover:text-red-600"
                          data-testid={`delete-student-${student.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="student-dialog" aria-describedby="student-dialog-description">
          <DialogHeader>
            <DialogTitle>
              {selectedStudent ? "Edit Student" : "Add New Student"}
            </DialogTitle>
            <p id="student-dialog-description" className="text-sm text-slate-500">
              {selectedStudent ? "Update student information" : "Add a new student to track their classes"}
            </p>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Student Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter student name"
                  required
                  data-testid="student-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="student@gmail.com"
                  data-testid="student-email-input"
                />
                <p className="text-xs text-slate-500">
                  Used for booking confirmations & cancellation notifications. Strongly recommended for all students.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="classes">Total Purchased Classes</Label>
                <Input
                  id="classes"
                  type="number"
                  min="0"
                  value={formData.total_purchased_classes}
                  onChange={(e) =>
                    setFormData({ ...formData, total_purchased_classes: e.target.value })
                  }
                  placeholder="Enter number of classes"
                  required
                  data-testid="student-classes-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="validity">Validity Date (Optional)</Label>
                <Input
                  id="validity"
                  type="date"
                  value={formData.validity_date}
                  onChange={(e) =>
                    setFormData({ ...formData, validity_date: e.target.value })
                  }
                  data-testid="student-validity-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                data-testid="cancel-student-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="save-student-btn"
              >
                {selectedStudent ? "Update" : "Add"} Student
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="delete-dialog" aria-describedby="delete-student-description">
          <DialogHeader>
            <DialogTitle className="text-amber-700">Archive Student</DialogTitle>
          </DialogHeader>
          <div id="delete-student-description" className="py-4 space-y-3">
            <p className="text-slate-600">
              Are you sure you want to archive <strong>{selectedStudent?.name}</strong>?
            </p>
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="text-emerald-700 text-sm font-medium">All data is preserved</p>
              <p className="text-emerald-600 text-xs mt-1">Class records and account info will be kept. You can restore this student anytime from <strong>Deleted Students</strong>.</p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              data-testid="cancel-delete-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-delete-btn"
            >
              Archive Student
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
